import type {
  Customer,
  ExportCustomersQuery,
  ListCustomersQuery,
} from '@clockwise/clients/customers';
import { ApiError } from '@clockwise/shared';
import { keepPreviousData, useMutation, useQuery } from '@tanstack/react-query';
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import {
  type ColumnDef,
  getCoreRowModel,
  type SortingState,
  useReactTable,
} from '@tanstack/react-table';
import { ChevronDown, Download, Filter, Pencil, Plus, Upload } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { z } from 'zod';
import { CustomFieldFilter } from '@/components/custom/custom-field-filter';
import { CustomerBudgetImportSheet } from '@/components/custom/customer-budget-import-sheet';
import { CustomerContactImportSheet } from '@/components/custom/customer-contact-import-sheet';
import {
  CUSTOMER_STATUS_FILTERS,
  customerFilterDefinitions,
  customerFilterStateToApiParams,
  customerFilterStateToUrl,
  DEFAULT_CUSTOMER_STATUS,
  urlToCustomerFilterState,
} from '@/components/custom/customer-filters';
import { CustomerFormSheet } from '@/components/custom/customer-form-sheet';
import { CustomerImportSheet } from '@/components/custom/customer-import-sheet';
import { StatusBadge } from '@/components/custom/customer-status-badge';
import { CustomerTeamImportSheet } from '@/components/custom/customer-team-import-sheet';
import { DataTable } from '@/components/custom/data-table';
import { FilterBar, type FilterState } from '@/components/custom/filter-bar/filter-bar';
import { RowActions } from '@/components/custom/row-actions';
import { SearchInput } from '@/components/custom/search-input';
import { SowImportSheet } from '@/components/custom/sow-import-sheet';
import { PageHeader } from '@/components/layout/page-header';
import { PageShell } from '@/components/layout/page-shell';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  customerBudgetsApi,
  customerContactsApi,
  customersApi,
  customerTeamApi,
  sowsApi,
} from '@/lib/api';
import { triggerDownload } from '@/lib/download';
import { useHasPermission } from '@/lib/permissions';
import { queryKeys } from '@/lib/query-keys';

type ExportDomain = 'customers' | 'contacts' | 'teams' | 'budgets' | 'sows';
type ImportTarget = 'customers' | 'contacts' | 'teams' | 'budgets' | 'sows';

const SORT_FIELD_VALUES = ['name', 'controllerName', 'parentName', 'status', 'createdAt'] as const;
type SortField = (typeof SORT_FIELD_VALUES)[number];

const listSearch = z.object({
  q: z.string().optional(),
  status: z.enum(CUSTOMER_STATUS_FILTERS).optional(),
  page: z.coerce.number().int().min(0).optional(),
  sortField: z.enum(SORT_FIELD_VALUES).optional(),
  sortDirection: z.enum(['asc', 'desc']).optional(),
  customFieldId: z.string().uuid().optional(),
  customFieldValue: z.string().optional(),
});
type ListSearch = z.infer<typeof listSearch>;

export const Route = createFileRoute('/_authenticated/customers/')({
  validateSearch: listSearch,
  component: CustomersPage,
});

const PAGE_SIZE = 25;
const DEFAULT_SORT_FIELD: SortField = 'name';

function CustomersPage() {
  const search = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });

  const sortField: SortField = search.sortField ?? DEFAULT_SORT_FIELD;
  const sortDirection: 'asc' | 'desc' = search.sortDirection ?? 'asc';
  const page = search.page ?? 0;
  const q = search.q ?? '';
  const statusFilter = search.status ?? DEFAULT_CUSTOMER_STATUS;
  const customFieldId = search.customFieldId ?? '';
  const customFieldValue = search.customFieldValue ?? '';

  const sorting: SortingState = [{ id: sortField, desc: sortDirection === 'desc' }];

  const updateSearch = (next: Partial<ListSearch>) =>
    navigate({
      search: (prev: ListSearch) => stripDefaults({ ...prev, ...next }),
      replace: true,
    });

  const filterState = useMemo(
    () => urlToCustomerFilterState({ status: search.status }),
    [search.status],
  );
  const apiFilters = useMemo(() => customerFilterStateToApiParams(filterState), [filterState]);
  const handleFilterChange = (state: FilterState) =>
    updateSearch({ ...customerFilterStateToUrl(state), page: 0 });

  // Filter shared by all three exports — same fields as the list query, minus
  // pagination (exports drain the whole filtered result).
  const exportFilter: ExportCustomersQuery = {
    q: q.trim() || undefined,
    ...apiFilters,
    customFieldId: customFieldId && customFieldValue ? customFieldId : undefined,
    customFieldValue: customFieldId && customFieldValue ? customFieldValue : undefined,
    sortField,
    sortDirection,
  };

  // `null` = closed, `{}` = the Add flow, `{ id }` = editing that row — one
  // sheet serves both so a row's pencil opens the same panel the header
  // button does, instead of the old fork into a full-page route.
  const [formSheet, setFormSheet] = useState<{ id?: string } | null>(null);
  const [importTarget, setImportTarget] = useState<ImportTarget | null>(null);

  // Export is gated on each domain's *view* permission; import is gated on
  // each domain's *edit* permission.
  const canViewCustomers = useHasPermission('customer.view');
  const canEditCustomers = useHasPermission('customer.edit');
  const canViewContacts = useHasPermission('customer.contact.view');
  const canEditContacts = useHasPermission('customer.contact.edit');
  const canExportTeam = useHasPermission('customer.team.view');
  const canEditTeam = useHasPermission('customer.team.edit');
  const canExportBudgets = useHasPermission('customer.budget.view');
  const canEditBudgets = useHasPermission('customer.budget.edit');
  const canExportSows = useHasPermission('customer.sow.view');
  const canEditSows = useHasPermission('customer.sow.edit');

  const canExportAny =
    canViewCustomers || canViewContacts || canExportTeam || canExportBudgets || canExportSows;
  const canImportAny =
    canEditCustomers || canEditContacts || canEditTeam || canEditBudgets || canEditSows;

  const exportMutation = useMutation({
    // Every export originates from this screen, so they all mirror the same
    // on-screen customer filters: customers export themselves, and the rest
    // export only rows for customers matching the filter.
    mutationFn: ({ domain }: { domain: ExportDomain }) => {
      if (domain === 'contacts') return customerContactsApi.exportFile(exportFilter);
      if (domain === 'teams') return customerTeamApi.exportFile(exportFilter);
      if (domain === 'budgets') return customerBudgetsApi.exportFile(exportFilter);
      if (domain === 'sows') return sowsApi.exportFile(exportFilter);
      return customersApi.exportFile(exportFilter);
    },
    onSuccess: ({ blob, filename }, { domain }) => {
      triggerDownload(blob, filename ?? `${domain}.xlsx`);
    },
    onError: (err) => {
      toast.error(err instanceof ApiError ? err.message : 'Export failed');
    },
  });

  // Debounced text search.
  const [searchInput, setSearchInput] = useState(q);
  useEffect(() => {
    setSearchInput(q);
  }, [q]);
  useEffect(() => {
    if (searchInput === q) return;
    const handle = setTimeout(() => updateSearch({ q: searchInput, page: 0 }), 300);
    return () => clearTimeout(handle);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchInput, q]);

  const listQuery: ListCustomersQuery = {
    q: q.trim() || undefined,
    ...apiFilters,
    sortField,
    sortDirection,
    customFieldId: customFieldId && customFieldValue ? customFieldId : undefined,
    customFieldValue: customFieldId && customFieldValue ? customFieldValue : undefined,
    limit: PAGE_SIZE,
    offset: page * PAGE_SIZE,
  };

  const query = useQuery({
    queryKey: queryKeys.customers.list(listQuery),
    queryFn: () => customersApi.list(listQuery),
    placeholderData: keepPreviousData,
  });

  const columns = useMemo<ColumnDef<Customer>[]>(
    () => [
      {
        accessorKey: 'name',
        header: 'Name',
        size: 320,
        cell: ({ row }) => (
          <Link
            to="/customers/$id"
            params={{ id: row.original.id }}
            className="block font-medium break-words text-primary underline-offset-2 hover:underline"
            onClick={(e) => e.stopPropagation()}
          >
            {row.original.name}
          </Link>
        ),
      },
      {
        accessorKey: 'controllerName',
        header: 'Controller',
        size: 240,
        cell: ({ row }) =>
          row.original.isInternal ? null : row.original.controllerName ? (
            <span className="block break-words">{row.original.controllerName}</span>
          ) : (
            <span className="text-muted-foreground">—</span>
          ),
      },
      {
        accessorKey: 'parentName',
        header: 'Parent',
        size: 220,
        // Projected server-side and NOT scope-gated, so a subsidiary always
        // names its parent even for a caller assigned only to the child. The
        // cell is plain text rather than a link: the row already navigates to
        // the child on click, and a nested link inside it reads as a mis-click
        // waiting to happen.
        cell: ({ row }) =>
          row.original.parentName ? (
            <span className="block break-words">{row.original.parentName}</span>
          ) : (
            <span className="text-muted-foreground">—</span>
          ),
      },
      {
        accessorKey: 'status',
        header: 'Status',
        size: 140,
        cell: ({ row }) => <StatusBadge status={row.original.status} />,
      },
      // Quick-edit shortcut so someone who already knows the row they want
      // doesn't pay the view-page round trip. Dropped from the column set
      // entirely (rather than rendered empty) without `customer.edit`, so a
      // read-only caller doesn't lose width to a dead column.
      //
      // Presence-only gate: `customer.edit` is scoped (all / my_organization /
      // assigned) but `useHasPermission` reads presence, not scope. A scoped
      // caller therefore sees the pencil on rows outside their scope and the
      // server rejects the save. Same gap the view page's Edit button already
      // has; closing it needs a per-row `editable` flag on the list response.
      ...(canEditCustomers
        ? [
            {
              id: 'actions',
              header: '',
              size: 72,
              enableSorting: false,
              cell: ({ row }) => (
                <RowActions>
                  <Button
                    size="icon-sm"
                    variant="ghost"
                    aria-label={`Edit ${row.original.name}`}
                    onClick={(e) => {
                      e.stopPropagation();
                      setFormSheet({ id: row.original.id });
                    }}
                  >
                    <Pencil className="size-4" />
                  </Button>
                </RowActions>
              ),
            } satisfies ColumnDef<Customer>,
          ]
        : []),
    ],
    [canEditCustomers],
  );

  const table = useReactTable({
    data: query.data?.data ?? [],
    columns,
    state: { sorting },
    onSortingChange: (updater) => {
      const next = typeof updater === 'function' ? updater(sorting) : updater;
      const first = next[0];
      if (!first) {
        updateSearch({ sortField: undefined, sortDirection: undefined });
        return;
      }
      updateSearch({
        sortField: first.id as SortField,
        sortDirection: first.desc ? 'desc' : 'asc',
        page: 0,
      });
    },
    getCoreRowModel: getCoreRowModel(),
    manualSorting: true,
    manualPagination: true,
    pageCount: Math.max(1, Math.ceil((query.data?.meta.total ?? 0) / PAGE_SIZE)),
  });

  const total = query.data?.meta.total ?? 0;
  const showingFrom = total === 0 ? 0 : page * PAGE_SIZE + 1;
  const showingTo = Math.min(total, page * PAGE_SIZE + (query.data?.data.length ?? 0));

  return (
    <PageShell>
      <PageHeader title="Customers" subtitle="The customer accounts we work with." count={total}>
        {canExportAny && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              {/* `DropdownMenuTrigger asChild` clobbers this Button's own
                  `data-slot="button"` with its own `data-slot="dropdown-menu-trigger"`
                  (Radix Slot lets the outer wrapper's explicit props win), so the
                  global `[data-slot='button']` pill-radius rule never matches this
                  trigger. Set the radius directly instead of depending on that rule. */}
              <Button
                variant="outline"
                disabled={exportMutation.isPending}
                className="rounded-full"
              >
                <Download />
                {exportMutation.isPending ? 'Exporting…' : 'Export'}
                <ChevronDown />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              {canViewCustomers && (
                <DropdownMenuItem onClick={() => exportMutation.mutate({ domain: 'customers' })}>
                  Customers
                </DropdownMenuItem>
              )}
              {canViewContacts && (
                <DropdownMenuItem onClick={() => exportMutation.mutate({ domain: 'contacts' })}>
                  Contacts
                </DropdownMenuItem>
              )}
              {canExportTeam && (
                <DropdownMenuItem onClick={() => exportMutation.mutate({ domain: 'teams' })}>
                  Teams
                </DropdownMenuItem>
              )}
              {canExportSows && (
                <DropdownMenuItem onClick={() => exportMutation.mutate({ domain: 'sows' })}>
                  SOWs
                </DropdownMenuItem>
              )}
              {canExportBudgets && (
                <DropdownMenuItem onClick={() => exportMutation.mutate({ domain: 'budgets' })}>
                  Budgets
                </DropdownMenuItem>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        )}
        {canImportAny && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              {/* Same `data-slot` clobbering as the Export trigger above —
                  set the pill radius directly. */}
              <Button variant="outline" className="rounded-full">
                <Upload />
                Import
                <ChevronDown />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              {canEditCustomers && (
                <DropdownMenuItem onClick={() => setImportTarget('customers')}>
                  Customers
                </DropdownMenuItem>
              )}
              {canEditContacts && (
                <DropdownMenuItem onClick={() => setImportTarget('contacts')}>
                  Contacts
                </DropdownMenuItem>
              )}
              {canEditTeam && (
                <DropdownMenuItem onClick={() => setImportTarget('teams')}>Teams</DropdownMenuItem>
              )}
              {canEditSows && (
                <DropdownMenuItem onClick={() => setImportTarget('sows')}>SOWs</DropdownMenuItem>
              )}
              {canEditBudgets && (
                <DropdownMenuItem onClick={() => setImportTarget('budgets')}>
                  Budgets
                </DropdownMenuItem>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        )}
        {canEditCustomers && (
          <Button onClick={() => setFormSheet({})}>
            <Plus />
            Add customer
          </Button>
        )}
      </PageHeader>

      <div
        data-surface="panel"
        className="flex flex-wrap items-center gap-3 rounded-md border bg-card p-6 py-4"
      >
        <span className="flex items-center gap-1.5 text-sm font-medium text-muted-foreground">
          <Filter className="size-3.5" />
          Filters:
        </span>
        <FilterBar
          definitions={customerFilterDefinitions}
          value={filterState}
          onChange={handleFilterChange}
        >
          <SearchInput
            placeholder="Search name, QB ID, phone…"
            value={searchInput}
            onValueChange={setSearchInput}
          />
        </FilterBar>
        <CustomFieldFilter
          object="customer"
          fieldId={customFieldId}
          value={customFieldValue}
          onChange={(id, val) =>
            updateSearch({
              customFieldId: id || undefined,
              customFieldValue: val || undefined,
              page: 0,
            })
          }
        />
      </div>

      <DataTable
        table={table}
        isLoading={query.isLoading}
        isError={query.isError}
        errorMessage={
          query.error instanceof ApiError ? query.error.message : 'Failed to load customers.'
        }
        emptyMessage={
          q || statusFilter !== DEFAULT_CUSTOMER_STATUS || (customFieldId && customFieldValue)
            ? 'No customers match these filters.'
            : 'No customers yet. Click “Add customer”.'
        }
        onRowClick={(c) => navigate({ to: '/customers/$id', params: { id: c.id } })}
      />

      <footer className="flex items-center justify-between text-xs text-muted-foreground">
        <span>
          {total === 0 ? 'No results' : `Showing ${showingFrom}–${showingTo} of ${total}`}
        </span>
        <div className="flex items-center gap-2">
          <Button
            size="sm"
            variant="outline"
            onClick={() => updateSearch({ page: Math.max(0, page - 1) })}
            disabled={page === 0 || query.isFetching}
          >
            Previous
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={() => updateSearch({ page: page + 1 })}
            disabled={(page + 1) * PAGE_SIZE >= total || query.isFetching}
          >
            Next
          </Button>
        </div>
      </footer>

      <CustomerFormSheet
        open={formSheet !== null}
        customerId={formSheet?.id}
        onOpenChange={(o) => {
          if (!o) setFormSheet(null);
        }}
      />
      <CustomerImportSheet
        open={importTarget === 'customers'}
        onOpenChange={(open) => setImportTarget(open ? 'customers' : null)}
      />
      <CustomerContactImportSheet
        open={importTarget === 'contacts'}
        onOpenChange={(open) => setImportTarget(open ? 'contacts' : null)}
      />
      <SowImportSheet
        open={importTarget === 'sows'}
        onOpenChange={(o) => setImportTarget(o ? 'sows' : null)}
      />
      <CustomerBudgetImportSheet
        open={importTarget === 'budgets'}
        onOpenChange={(o) => setImportTarget(o ? 'budgets' : null)}
      />
      <CustomerTeamImportSheet
        open={importTarget === 'teams'}
        onOpenChange={(open) => setImportTarget(open ? 'teams' : null)}
      />
    </PageShell>
  );
}

function stripDefaults(s: ListSearch): ListSearch {
  const out: ListSearch = {};
  if (s.q && s.q.length > 0) out.q = s.q;
  if (s.status && s.status !== DEFAULT_CUSTOMER_STATUS) out.status = s.status;
  if (s.page && s.page > 0) out.page = s.page;
  if (s.sortField && s.sortField !== DEFAULT_SORT_FIELD) out.sortField = s.sortField;
  if (s.sortDirection && s.sortDirection !== 'asc') out.sortDirection = s.sortDirection;
  if (s.customFieldId && s.customFieldValue) {
    out.customFieldId = s.customFieldId;
    out.customFieldValue = s.customFieldValue;
  }
  return out;
}
